// Copyright (c) StrangeBee 2024

import { useCallback, useMemo, useState } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import { FormatDate } from '@Molecules/Format';
import { ColumnType } from 'antd/lib/table';
import { THList } from '@Molecules/THList';
import { AttachmentType, ObjectTypeV1, TaskLogOutput, TaskStatus } from 'thehive-sdk';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { ICONS } from '@Constants/iconsApp';
import { formatFileSize } from '@Helpers/stats';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { copyToClipboard } from '@Helpers/copy';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import useConfigList from '@Hooks/useConfigList';
import client from '@Client';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { Link } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { AttachmentListProps } from '../types';
import usePageTitle from '@Hooks/usePageTitle';
interface AttachmentDataType extends AttachmentType {
    extraData: TaskLogOutput['extraData'];
    logId: TaskLogOutput['_id'];
}

export const AttachmentTasksList = ({
    describe,
    parentId,
    parentNumber,
    previewManager,
}: Omit<AttachmentListProps, 'hasPermissions'>) => {
    const i = useLocalMessage();
    usePageTitle(i('page-title.cases.task-attachments', { number: parentNumber || 0 }));

    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(false);

    const {
        filterManager,
        storagePageManager,
        dataSource = [],
        columnsRenderer,
        paginationManager,
        table,
        status = 'loading',
    } = useConfigList<AttachmentDataType>({
        selector: {
            head: [
                { name: 'getCase', idOrName: parentId },
                { name: 'tasks' },
                { filter: { field: 'status', values: TaskStatus.CANCEL, operator: 'not', type: 'string' } },
                { name: 'logs' },
                { filter: { operator: 'contains', values: 'attachments.id', type: 'string' } },
            ],
            extraData: ['task', 'taskId'],
            name: 'get-case-logs-attachments',
        },
        controller: client.CaseController,
        storageId: StorageFilterEnum.TASKS_ATTACHMENTS,
        objectType: ObjectTypeV1.CASE,
        streamRootId: parentId,
    });

    const getDownloadUrl = useCallback(
        (attachmentId: string, logId: string): string =>
            window.location.origin + client.apiUrl + `/v1/log/${logId}/attachment/${attachmentId}/download`,
        [],
    );

    const setLocalFilters = useCallback(
        (localFilters: boolean): void => {
            setLocalFilterIsOpen(localFilters);
            storagePageManager?.setFilterIsOpen?.(localFilters);
            filterManager.setEditFilters(localFilters);
        },
        [filterManager, storagePageManager],
    );

    const getColumns = useCallback(
        (): ColumnType<AttachmentDataType>[] =>
            columnsRenderer([
                {
                    title: [
                        {
                            label: i('case.attachment.table.filename'),
                            sortField: 'attachments.name',
                        },
                    ],
                    className: 'case-attachment-list__column-filename custom-table__cell',
                    render: (attachement) => {
                        const { name, contentType, logId } = attachement;

                        return previewManager.allowPreview(contentType) ? (
                            <THButton
                                type="link"
                                onClick={() => previewManager.togglePreview(attachement, { type: 'log', id: logId })}
                            >
                                {name}
                            </THButton>
                        ) : (
                            <span>{name}</span>
                        );
                    },
                },
                {
                    title: [
                        {
                            label: i('case.attachment.table.task'),
                            sortField: 'message',
                        },
                    ],
                    className: 'case-attachment-list__column-task custom-table__cell',
                    render: ({ extraData }) => (
                        <Link to={RouteBuilder.compute('cases.task', { caseId: parentId, taskId: extraData?.taskId })}>
                            {extraData.task?.title}
                        </Link>
                    ),
                },
                {
                    title: [
                        {
                            label: i('case.attachment.table.type'),
                            sortField: 'attachments.contentType',
                        },
                    ],
                    className: 'case-attachment-list__column-type custom-table__cell',
                    render: ({ contentType }) => <span>{contentType}</span>,
                },
                {
                    title: [
                        {
                            label: i('case.attachment.table.size'),
                            sortField: 'attachments.size',
                        },
                    ],
                    className: 'case-attachment-list__column-size custom-table__cell',
                    render: ({ size }) => <span>{formatFileSize(size)}</span>,
                },
                {
                    title: [
                        {
                            label: i('case.attachment.table.date'),
                            sortField: '_createdAt',
                        },
                    ],
                    className: 'case-attachment-list__column-date custom-table__cell',
                    render: ({ _createdAt = 0 }) => <FormatDate date={_createdAt} />,
                },
                {
                    className: 'case-attachment-list__column-download custom-table__cell',
                    render: ({ _id, logId }) => (
                        <ActionsList
                            actionList={{
                                copy: {
                                    label: i('general.copy-url'),
                                    icon: <ICONS.CopyOutlined />,
                                    query: () =>
                                        copyToClipboard(getDownloadUrl(_id || '', logId), i('notification.copy')),
                                },
                                export: {
                                    label: i('general.download'),
                                    icon: <ICONS.DownloadOutlined />,
                                    CustomComponent: (triggerElem) => (
                                        <ExternalLink url={getDownloadUrl(_id || '', logId)}>
                                            {triggerElem}
                                        </ExternalLink>
                                    ),
                                },
                            }}
                            scope="single"
                            isIcon
                        />
                    ),
                },
            ]),
        [parentId, columnsRenderer, getDownloadUrl, i, previewManager],
    );

    const data: AttachmentDataType[] = useMemo(() => {
        const castedData = dataSource as unknown as TaskLogOutput[];
        return castedData.flatMap(({ attachments, extraData, _id }) =>
            attachments.map((attachment) => ({ ...attachment, extraData, logId: _id })),
        );
    }, [dataSource]);

    return (
        <THList>
            <THList.Header className="case-attachment-header">
                <THList.Slot name="MANAGE">
                    <div />
                </THList.Slot>
                <THList.Slot name="MANAGE">
                    <ManageSwitch
                        label={i('common.filters')}
                        value={localFilterIsOpen}
                        onChange={setLocalFilters}
                        icon={<ICONS.FilterOutlined />}
                    />
                </THList.Slot>
                <THList.Slot name="CONTENT">
                    {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                        <THFilter filterConfig={filterManager} describeField={describe?.[ObjectTypeV1.LOG]}>
                            <Footer filterConfig={filterManager} />
                        </THFilter>
                    )}
                </THList.Slot>
            </THList.Header>

            <THList.Content
                dataSource={data}
                columns={getColumns()}
                rowKey="_id"
                emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('case.attachment.none')} />}
            />

            <THList.Footer>
                <THList.Slot name="NAVIGATION">
                    <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                    <PaginationSize paginationConfig={paginationManager} />
                </THList.Slot>
            </THList.Footer>
        </THList>
    );
};
